import {t, useLanguage, interfaceLocale} from '../i18n';
import {compactReminderNotifications} from '../notifications/inboxPolicy';
import './NotificationCenterPolish.css';
// src/components/NotificationCenter.jsx
// LOOP NOTIFICATIONS CLEAN INBOX V4.3 — advanced settings devices full scroll
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { auth, db as defaultDb, messaging as defaultMessaging } from "../firebase";
import { getToken, isSupported as isMessagingSupported } from "firebase/messaging";
import { openLoopNotificationRoute } from "../notifications/notificationRouting";
import { getCurrentEffectiveSession, isEffectiveSessionLoggedIn } from "../auth/effectiveSession";
import { makeUserScopedStorageKey } from "../auth/authStorage";
import getUserBusinessesAndRoles, { getCachedUserBusinesses } from "../services/userBusinesses";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";
import { getEffectiveManualAuthSession, getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import "../styles/loopLayers.css";

const MAX_ITEMS = 80;
const FILTER_STORAGE_KEY = "loop_notification_center_filters_v2";

function norm(value) {
  return String(value || "").trim().toLowerCase();
}

function uniqueNormalizedStrings(values = []) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const cleanValue = norm(value);
    if (!cleanValue || seen.has(cleanValue)) return;
    seen.add(cleanValue);
    out.push(cleanValue);
  });
  return out;
}

function decodeNotificationJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const part = token.split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

function manualAuthToNotificationUser(ma) {
  try {
    if (!ma || !isManualAuthLikelyValid(ma)) return null;
    const raw = ma.raw && typeof ma.raw === "object" ? ma.raw : {};
    const idToken = ma.idToken || raw.idToken || raw.id_token || "";
    const payload = decodeNotificationJwtPayload(idToken) || {};
    const firebaseEmails = Array.isArray(payload?.firebase?.identities?.email) ? payload.firebase.identities.email : [];
    const uid = String(
      ma.uid || ma.userId || raw.uid || raw.userId || payload.user_id || payload.uid || payload.sub || ""
    ).trim();
    const emailAliases = uniqueNormalizedStrings([
      ma.email,
      ma.userEmail,
      ma.contactEmail,
      ma.authEmail,
      raw.email,
      raw.userEmail,
      payload.email,
      ...firebaseEmails,
    ]);
    const displayName = String(
      ma.displayName || ma.name || raw.displayName || raw.name || payload.name || payload.displayName || ""
    ).trim();

    if (!uid && !emailAliases.length) return null;

    return { uid, email: emailAliases[0] || "", emailAliases, displayName };
  } catch (_) {
    return null;
  }
}

function getManualNotificationUser() {
  try {
    const session = typeof getEffectiveManualAuthSession === "function" ? getEffectiveManualAuthSession() : null;
    const fromSession = manualAuthToNotificationUser(session?.manualAuth || null);
    if (fromSession) {
      return {
        ...fromSession,
        uid: fromSession.uid || session?.user?.uid || "",
        email: fromSession.email || norm(session?.user?.email || ""),
        emailAliases: uniqueNormalizedStrings([
          fromSession.email,
          ...(Array.isArray(fromSession.emailAliases) ? fromSession.emailAliases : []),
          session?.user?.email,
        ]),
        displayName: fromSession.displayName || session?.user?.displayName || "",
      };
    }
  } catch (_) {}

  try {
    return manualAuthToNotificationUser(getManualAuth());
  } catch (_) {
    return null;
  }
}

function getEffectiveUser(explicitUser = null) {
  const manualUser = getManualNotificationUser();
  try {
    const session = getCurrentEffectiveSession({
      firebaseAuth: auth,
      userInfo: explicitUser || manualUser || {},
    });
    if (isEffectiveSessionLoggedIn(session)) {
      const emailAliases = uniqueNormalizedStrings([
        session.user?.email,
        explicitUser?.email,
        explicitUser?.contactEmail,
        manualUser?.email,
        ...(Array.isArray(manualUser?.emailAliases) ? manualUser.emailAliases : []),
      ]);
      return {
        uid: session.user?.uid ? String(session.user.uid) : (explicitUser?.uid || manualUser?.uid || ""),
        email: emailAliases[0] || "",
        emailAliases,
        displayName: session.user?.displayName || explicitUser?.displayName || explicitUser?.nombre || explicitUser?.name || manualUser?.displayName || "",
      };
    }
  } catch (_) {}

  if (manualUser) return manualUser;

  return {
    uid: explicitUser?.uid ? String(explicitUser.uid) : "",
    email: norm(explicitUser?.email || explicitUser?.contactEmail || ""),
    emailAliases: uniqueNormalizedStrings([explicitUser?.email, explicitUser?.contactEmail]),
    displayName: explicitUser?.displayName || explicitUser?.nombre || explicitUser?.name || "",
  };
}

function notificationClientTimestamp(useClientTimestamp = false) {
  return useClientTimestamp ? new Date().toISOString() : serverTimestamp();
}

function normalizeNotificationFsRow(row = {}, fallbackId = "") {
  const id = String(row.id || row.docId || row._id || fallbackId || (row.path ? String(row.path).split("/").pop() : "") || "").trim();
  return normalizeNotification(id || `notification_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, row || {});
}

function timestampToMs(value) {
  try {
    if (!value) return 0;
    if (typeof value === "number") return value > 100000000000 ? value : value * 1000;
    if (typeof value === "string") {
      const t = new Date(value).getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (value.seconds) return Number(value.seconds) * 1000;
    if (value._seconds) return Number(value._seconds) * 1000;
  } catch (_) {}
  return 0;
}

function relativeTime(value) {
  const ms = timestampToMs(value);
  if (!ms) return "Ahora";
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return "Ahora";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d`;
  try {
    return new Date(ms).toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short" });
  } catch (_) {
    return "";
  }
}

function fullDateLabel(value) {
  const ms = timestampToMs(value);
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(interfaceLocale(), {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
}

function notificationCategory(type) {
  const t = norm(type);
  // Order matters: review/payment/inventory notifications may also include bookingId.
  if (t.includes("review") || t.includes("rating") || t.includes("valor")) return "reviews";
  if (t.includes("payment") || t.includes("pago") || t.includes("paid") || t.includes("refund") || t.includes("reembolso")) return "payments";
  if (t.includes("inventory") || t.includes("inventario") || t.includes("stock")) return "inventory";
  if (t.includes("membership") || t.includes("membres") || t.includes("loyalty") || t.includes("fidelidad") || t.includes("reward") || t.includes("recompensa") || t.includes("benefit") || t.includes("beneficio")) return "benefits";
  if (t.includes("reminder") || t.includes("recordatorio")) return "reminders";
  if (t.includes("cancel") || t.includes("reject") || t.includes("declin") || t.includes("updated") || t.includes("change") || t.includes("cambio")) return "changes";
  if (t.includes("confirm") || t.includes("approved") || t.includes("booking") || t.includes("reservation") || t.includes("reserva")) return "bookings";
  return "other";
}

function iconForType(type) {
  const category = notificationCategory(type);
  if (category === "changes") return "✕";
  if (category === "reminders") return "⏰";
  if (category === "reviews") return "★";
  if (category === "payments") return "$";
  if (category === "inventory") return "▦";
  if (category === "benefits") return "◇";
  if (norm(type).includes("confirm") || norm(type).includes("approved")) return "✓";
  if (category === "bookings") return "📅";
  return "•";
}

function labelForCategory(category) {
  if (category === "bookings") return "Reservas";
  if (category === "reminders") return "Recordatorios";
  if (category === "changes") return "Cambios";
  if (category === "reviews") return "Valoraciones";
  if (category === "payments") return "Pagos";
  if (category === "inventory") return "Inventario";
  if (category === "benefits") return "Beneficios";
  return "Otros";
}

function notificationBusinessLabel(item = {}) {
  return String(item.businessName || item.data?.businessName || item.businessId || item.data?.businessId || "").trim();
}

function notificationServiceLabel(item = {}) {
  const candidates = [
    item.serviceName,
    item.data?.serviceName,
    item.data?.service,
    item.data?.bookingServiceName,
    item.data?.packageName,
    item.data?.title,
  ];
  for (const value of candidates) {
    const cleanValue = String(value || "").trim();
    if (cleanValue && !/^califica tu servicio$/i.test(cleanValue)) return cleanValue;
  }

  const body = String(item.body || item.data?.body || item.data?.message || "").trim();
  const beforeReady = body.match(/^(.+?)\s+(?:ya puede|puede|está|esta)\s+/i);
  if (beforeReady && beforeReady[1]) return beforeReady[1].trim();
  return "Servicio";
}

function compactNotificationBody(items = []) {
  const names = Array.from(new Set(
    items.map(notificationServiceLabel).map((x) => String(x || "").trim()).filter(Boolean)
  ));
  if (!names.length) return `${items.length} servicios listos para calificar.`;
  if (names.length === 1) return `${names[0]} y ${Math.max(0, items.length - 1)} más`;
  if (names.length === 2) return `${names[0]}, ${names[1]}${items.length > 2 ? ` y ${items.length - 2} más` : ""}`;
  return `${names[0]}, ${names[1]} y ${items.length - 2} más`;
}

function shouldCompactNotification(item = {}) {
  const type = norm(item.type || item.data?.type || item.data?.notificationType || "");
  return item.category === "reviews" || type.includes("review") || type.includes("rating") || type.includes("valor");
}

function notificationDayBucket(item = {}) {
  const ms = Number(item.createdAtMs || timestampToMs(item.createdAt));
  if (!ms) return "recent";
  return new Date(ms).toDateString();
}

function normalizeNotification(id, data = {}) {
  const type = data.type || data.notificationType || "notification";
  const businessName = data.businessName || data.business?.name || data.data?.businessName || "";
  const businessId = data.businessId || data.data?.businessId || "";
  const bookingId = data.bookingId || data.data?.bookingId || "";
  const createdAt = data.createdAt || data.createdAtMs || data.sentAt || null;
  return {
    id,
    type,
    category: notificationCategory(type),
    title: data.title || data.heading || "Notificación",
    body: data.body || data.message || data.copy || "",
    businessName,
    businessId,
    bookingId,
    route: data.route || data.data?.route || "",
    read: data.read === true,
    createdAt,
    createdAtMs: timestampToMs(createdAt),
    targetRole: data.targetRole || data.data?.targetRole || "",
    data,
  };
}

function notificationFilterKey(actor = {}) {
  return makeUserScopedStorageKey(FILTER_STORAGE_KEY, actor || {});
}

function readSavedFilters(actor = {}) {
  try {
    const raw = localStorage.getItem(notificationFilterKey(actor));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch (_) {
    return {};
  }
}

function saveFilters(next, actor = {}) {
  try {
    localStorage.setItem(notificationFilterKey(actor), JSON.stringify(next || {}));
  } catch (_) {}
}


const DEVICE_SETTINGS_VERSION = "v1";
const DEVICE_STORAGE_KEY = "loop_notification_device_id_v1";
const DEFAULT_DEVICE_SETTINGS = {
  scopeMode: "all", // all | active | custom
  businessScopes: [],
  notificationTypes: {
    bookings: true,
    reminders: true,
    changes: true,
    reviews: true,
    payments: true,
    inventory: true,
    benefits: true,
  },
  muteManualCreatedByMe: true,
};

function safeTokenDocId(token) {
  return String(token || "")
    .trim()
    .replace(/\//g, "_")
    .replace(/\./g, "_")
    .replace(/#/g, "_")
    .replace(/\[/g, "_")
    .replace(/\]/g, "_")
    .slice(0, 420);
}

function getOrCreateDeviceId() {
  try {
    const existing = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (existing) return existing;
    const random = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `device_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_STORAGE_KEY, random);
    return random;
  } catch (_) {
    return `device_${Date.now()}`;
  }
}

function getDeviceLabel() {
  try {
    const ua = navigator?.userAgent || "";
    const browser = ua.includes("Edg/") ? "Edge" : ua.includes("Chrome/") ? "Chrome" : ua.includes("Safari/") ? "Safari" : "Navegador";
    const os = ua.includes("Windows") ? "Windows" : ua.includes("Mac") ? "Mac" : ua.includes("Android") ? "Android" : ua.includes("iPhone") || ua.includes("iPad") ? "iOS" : "Web";
    return `${browser} en ${os}`;
  } catch (_) {
    return "Este dispositivo";
  }
}

function mergeDeviceSettings(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const types = source.notificationTypes && typeof source.notificationTypes === "object" ? source.notificationTypes : {};
  const scopes = Array.isArray(source.businessScopes) ? source.businessScopes.map((x) => String(x || "").trim()).filter(Boolean) : [];
  const scopeMode = ["all", "active", "custom"].includes(source.scopeMode) ? source.scopeMode : (source.notifyAllBusinesses === false ? (scopes.length ? "custom" : "active") : "all");
  return {
    ...DEFAULT_DEVICE_SETTINGS,
    ...source,
    scopeMode,
    businessScopes: scopes,
    notificationTypes: {
      ...DEFAULT_DEVICE_SETTINGS.notificationTypes,
      ...Object.fromEntries(Object.keys(DEFAULT_DEVICE_SETTINGS.notificationTypes).map((key) => [key, typeof types[key] === "boolean" ? types[key] : DEFAULT_DEVICE_SETTINGS.notificationTypes[key]])),
    },
    muteManualCreatedByMe: source.muteManualCreatedByMe !== false,
  };
}


function normalizeDeviceBusinessOption(row = {}) {
  if (!row || typeof row !== "object") return null;
  const rawId =
    row.businessId ||
    row.id ||
    row.business?.id ||
    row.businessDocId ||
    row.docId ||
    row.value ||
    "";
  const id = String(rawId || "").trim();
  if (!id) return null;

  const rawName =
    row.businessName ||
    row.name ||
    row.business?.name ||
    row.business?.businessName ||
    row.label ||
    id;

  const label = String(rawName || id).trim() || id;
  const role = String(row.role || row.accessRole || row.businessRole || "").trim().toLowerCase();
  const location = String(
    row.location ||
    row.address ||
    row.business?.location ||
    row.business?.address ||
    row.business?.city ||
    ""
  ).trim();

  return {
    value: id,
    label,
    role,
    location,
    source: row.source || "access",
  };
}

function mergeBusinessOptionLists(...lists) {
  const map = new Map();

  lists.flat().forEach((item) => {
    const normalized = normalizeDeviceBusinessOption(item);
    if (!normalized) return;
    const key = normalized.value;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, normalized);
      return;
    }

    map.set(key, {
      ...existing,
      ...normalized,
      label: existing.label && existing.label !== existing.value ? existing.label : normalized.label,
      role: existing.role || normalized.role,
      location: existing.location || normalized.location,
    });
  });

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}


function deviceScopeLabel(device = {}, businessOptions = []) {
  const scopeMode = String(device.scopeMode || (device.notifyAllBusinesses === false ? "custom" : "all")).trim();
  if (scopeMode === "all" || device.notifyAllBusinesses === true) return "Todos los negocios";

  const scopes = Array.isArray(device.businessScopes) ? device.businessScopes.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!scopes.length) return "Sin negocios elegidos";

  const labels = scopes
    .map((id) => {
      const match = businessOptions.find((option) => String(option.value) === String(id));
      return match?.label || id;
    })
    .filter(Boolean);

  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return labels.join(" + ");
  return `${labels.length} negocios`;
}

function formatDeviceSeenAt(value) {
  const ms = timestampToMs(value);
  if (!ms) return "Sin actividad reciente";
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return "Activo ahora";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d`;
  try {
    return new Date(ms).toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short" });
  } catch (_) {
    return "Actividad guardada";
  }
}



async function maybeGetBrowserPushToken() {
  try {
    if (!defaultMessaging) return "";
    const supported = await isMessagingSupported().catch(() => false);
    if (!supported) return "";
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      const permission = await Notification.requestPermission().catch(() => "default");
      if (permission !== "granted") return "";
    }
    if (typeof Notification !== "undefined" && Notification.permission !== "granted") return "";
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || process.env.REACT_APP_FCM_VAPID_KEY || "";
    const token = await getToken(defaultMessaging, vapidKey ? { vapidKey } : undefined).catch(() => "");
    return token || "";
  } catch (_) {
    return "";
  }
}


const styles = `
  .loop-notification-center-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  .loop-notification-panel-backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--loop-z-header-panel-backdrop, 1150);
    background: transparent;
  }

  .loop-notification-panel {
    position: fixed;
    top: var(--loop-notification-top, 82px);
    right: var(--loop-notification-right, 18px);
    width: min(430px, calc(100vw - 24px));
    max-height: min(690px, calc(100dvh - 106px));
    overflow: hidden;
    z-index: var(--loop-z-header-panel, 1200);
    border-radius: 28px;
    border: 1px solid rgba(226, 232, 240, 0.94);
    background: rgba(255, 255, 255, 0.985);
    box-shadow: 0 28px 80px rgba(15, 23, 42, 0.20);
    color: #0f172a;
    display: flex;
    flex-direction: column;
    min-height: 0;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }

  .loop-notification-panel-head {
    padding: 18px 18px 13px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.78);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    background: linear-gradient(135deg, rgba(0, 103, 95, 0.08), rgba(255, 255, 255, 0.98) 70%);
  }

  .loop-notification-panel-head span {
    display: block;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: #00675f;
    margin-bottom: 3px;
  }

  .loop-notification-panel-head h3 {
    margin: 0;
    font-size: 22px;
    line-height: 1.04;
    letter-spacing: -0.04em;
    color: #0f172a;
  }

  .loop-notification-panel-head p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
  }

  .loop-notification-mark-all {
    border: 1px solid rgba(0, 103, 95, 0.15);
    background: #ecfdf5;
    color: #00675f;
    border-radius: 999px;
    padding: 8px 10px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .loop-notification-toolbar {
    padding: 10px 10px 8px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.70);
    display: grid;
    gap: 8px;
    background: #ffffff;
  }

  .loop-notification-scroll-shell {
    position: relative;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) 24px;
    align-items: center;
    gap: 3px;
  }

  .loop-notification-scroll-shell.is-view {
    grid-template-columns: minmax(0, 1fr);
  }

  .loop-notification-scroll-shell.is-view .loop-notification-scroll-btn {
    display: none;
  }

  .loop-notification-tabs,
  .loop-notification-filter-row {
    display: flex;
    flex-wrap: nowrap;
    gap: 7px;
    min-width: 0;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    scrollbar-width: none;
    scroll-snap-type: x proximity;
    padding: 1px 2px 4px;
  }

  .loop-notification-tabs::-webkit-scrollbar,
  .loop-notification-filter-row::-webkit-scrollbar { display: none; }

  .loop-notification-scroll-btn {
    width: 24px;
    height: 30px;
    border: 1px solid rgba(0, 103, 95, .14);
    border-radius: 999px;
    background: linear-gradient(180deg, #ffffff, #f8fafc);
    color: #00675f;
    font-size: 17px;
    line-height: 1;
    font-weight: 950;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 14px rgba(15, 23, 42, .05);
  }

  .loop-notification-chip {
    flex: 0 0 auto;
    scroll-snap-align: start;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #475569;
    border-radius: 999px;
    padding: 7px 10px;
    font-size: 11px;
    line-height: 1;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .loop-notification-chip.is-active {
    background: #00675f;
    border-color: #00675f;
    color: #ffffff;
    box-shadow: 0 9px 20px rgba(0, 103, 95, 0.18);
  }

  .loop-notification-business-scope {
    min-height: 30px;
    border-radius: 999px;
    border: 1px solid rgba(0,103,95,.16);
    background: #f0fdfa;
    color: #00675f;
    display: inline-flex;
    align-items: center;
    width: max-content;
    max-width: 100%;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 900;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .loop-notification-filter-select {
    width: 100%;
    height: 36px;
    border-radius: 13px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #0f172a;
    padding: 0 11px;
    font-size: 12px;
    font-weight: 900;
    outline: none;
  }

  .loop-notification-list {
    overflow: auto;
    padding: 8px;
    display: grid;
    gap: 6px;
  }

  .loop-notification-section-label {
    padding: 8px 9px 4px;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .10em;
    text-transform: uppercase;
  }

  .loop-notification-item {
    width: 100%;
    text-align: left;
    border: 1px solid transparent;
    border-radius: 20px;
    background: transparent;
    padding: 12px;
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) auto;
    gap: 11px;
    cursor: pointer;
    color: inherit;
  }

  .loop-notification-item:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
  }

  .loop-notification-item.is-unread {
    background: #f0fdfa;
    border-color: rgba(20, 184, 166, 0.22);
  }

  .loop-notification-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    background: #00675f;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 950;
    font-size: 15px;
    box-shadow: 0 10px 24px rgba(0, 103, 95, 0.20);
  }

  .loop-notification-copy { min-width: 0; }

  .loop-notification-copy strong {
    display: block;
    font-size: 13px;
    line-height: 1.15;
    font-weight: 950;
    color: #0f172a;
    margin-bottom: 4px;
  }

  .loop-notification-copy p {
    margin: 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
  }

  .loop-notification-copy em {
    display: block;
    margin-top: 5px;
    font-size: 11px;
    font-style: normal;
    font-weight: 850;
    color: #00675f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loop-notification-meta {
    margin-top: 7px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .loop-notification-pill {
    border-radius: 999px;
    background: #f1f5f9;
    color: #64748b;
    padding: 4px 7px;
    font-size: 10px;
    font-weight: 950;
  }

  .loop-notification-time {
    color: #94a3b8;
    font-size: 11px;
    font-weight: 900;
    padding-top: 2px;
    white-space: nowrap;
  }

  .loop-notification-empty {
    padding: 34px 20px 38px;
    text-align: center;
  }

  .loop-notification-empty div {
    width: 54px;
    height: 54px;
    margin: 0 auto 13px;
    border-radius: 20px;
    display: grid;
    place-items: center;
    background: #ecfdf5;
    color: #00675f;
    font-weight: 950;
    font-size: 20px;
  }

  .loop-notification-empty strong {
    display: block;
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .loop-notification-empty p {
    margin: 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
  }



  .loop-notification-head-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .loop-notification-settings-btn {
    border: 1px solid rgba(226, 232, 240, 0.96);
    background: rgba(255,255,255,.88);
    color: #334155;
    border-radius: 999px;
    padding: 8px 10px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .loop-notification-settings-btn.is-active {
    color: #00675f;
    border-color: rgba(0,103,95,.18);
    background: #ecfdf5;
  }

  .loop-notification-settings-panel {
    padding: 12px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.72);
    background: linear-gradient(180deg, #ffffff, #fbfffd);
    display: grid;
    gap: 10px;
  }

  .loop-notification-settings-card {
    border: 1px solid rgba(226,232,240,.92);
    background: #fff;
    border-radius: 18px;
    padding: 12px;
    display: grid;
    gap: 10px;
  }

  .loop-notification-settings-card h4 {
    margin: 0;
    font-size: 14px;
    letter-spacing: -0.02em;
  }

  .loop-notification-settings-card p {
    margin: -4px 0 0;
    color: #64748b;
    font-size: 11.5px;
    line-height: 1.35;
    font-weight: 750;
  }

  .loop-notification-radio-grid,
  .loop-notification-toggle-grid,
  .loop-notification-business-grid {
    display: grid;
    gap: 7px;
  }

  .loop-notification-radio-grid {
    grid-template-columns: 1fr;
  }

  .loop-notification-toggle-grid,
  .loop-notification-business-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .loop-notification-setting-option {
    min-height: 38px;
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 14px;
    padding: 8px 10px;
    color: #334155;
    font-size: 11.5px;
    font-weight: 900;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 9px;
    text-align: left;
  }

  .loop-notification-setting-option.is-active {
    background: #ecfdf5;
    border-color: rgba(20,184,166,.36);
    color: #00675f;
    box-shadow: 0 8px 18px rgba(0,103,95,.07);
  }

  .loop-notification-setting-option span {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    border: 1px solid #cbd5e1;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    color: transparent;
    font-size: 10px;
  }

  .loop-notification-setting-option.is-active span {
    background: #00796f;
    border-color: #00796f;
    color: #fff;
  }

  .loop-notification-settings-footer {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .loop-notification-settings-status {
    margin: 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
  }

  .loop-notification-save-settings {
    border: 1px solid #00796f;
    background: #00796f;
    color: #fff;
    border-radius: 999px;
    padding: 9px 13px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(0,103,95,.16);
    white-space: nowrap;
  }

  .loop-notification-device-name {
    display: grid;
    gap: 6px;
  }

  .loop-notification-device-name label {
    color: #64748b;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  .loop-notification-device-input {
    width: 100%;
    height: 42px;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #0f172a;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 850;
    outline: none;
    box-sizing: border-box;
  }

  .loop-notification-device-input:focus {
    border-color: rgba(20,184,166,.44);
    box-shadow: 0 0 0 4px rgba(20,184,166,.12);
    background: #ffffff;
  }

  .loop-notification-settings-note {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    border-radius: 15px;
    background: #f8fafc;
    border: 1px solid rgba(226,232,240,.88);
    padding: 10px;
    color: #64748b;
    font-size: 11px;
    line-height: 1.35;
    font-weight: 800;
  }

  .loop-notification-settings-note strong {
    color: #00675f;
    font-weight: 950;
  }

  .loop-notification-device-list {
    display: grid;
    gap: 8px;
  }

  .loop-notification-device-row {
    border: 1px solid rgba(226,232,240,.92);
    background: #ffffff;
    border-radius: 16px;
    padding: 10px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .loop-notification-device-row strong {
    display: block;
    color: #0f172a;
    font-size: 12.5px;
    line-height: 1.15;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loop-notification-device-row small {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 10.5px;
    line-height: 1.25;
    font-weight: 800;
  }

  .loop-notification-device-tag {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: 999px;
    padding: 0 8px;
    border: 1px solid rgba(0,103,95,.16);
    background: #ecfdf5;
    color: #00675f;
    font-size: 10px;
    font-weight: 950;
    white-space: nowrap;
  }

  .loop-notification-client-mode-card {
    border: 1px solid rgba(20,184,166,.20);
    background:
      radial-gradient(circle at 0% 0%, rgba(20,184,166,.10), transparent 34%),
      #ffffff;
  }

  .loop-notification-client-mode-pill {
    display: inline-flex;
    width: fit-content;
    align-items: center;
    min-height: 26px;
    border-radius: 999px;
    padding: 0 9px;
    background: #ecfdf5;
    border: 1px solid rgba(20,184,166,.24);
    color: #00675f;
    font-size: 10.5px;
    font-weight: 950;
    letter-spacing: .02em;
  }

  @media (max-width: 640px) {
    .loop-notification-panel {
      top: calc(env(safe-area-inset-top, 0px) + 70px);
      right: 8px;
      left: 8px;
      width: auto;
      max-height: min(640px, calc(100dvh - 92px));
      border-radius: 24px;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS — SETTINGS SCROLL + STICKY SAVE FIX
   La vista de Ajustes puede ser más larga que el panel.
   Este bloque permite scroll interno y mantiene Guardar visible.
   ========================================================= */

  .loop-notification-panel > * {
    min-height: 0;
  }

  .loop-notification-settings-panel {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    scrollbar-gutter: stable;
  }

  .loop-notification-panel.is-settings-mode {
    background: rgba(255, 255, 255, 0.992);
  }

  .loop-notification-panel.is-settings-mode .loop-notification-panel-head {
    border-bottom-color: rgba(226,232,240,.62);
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-panel {
    border-bottom: 0;
    background:
      radial-gradient(circle at 0% 0%, rgba(20,184,166,.08), transparent 30%),
      linear-gradient(180deg, #ffffff 0%, #fbfffd 100%);
  }

  .loop-notification-settings-panel-clean-note {
    margin: -2px 2px 0;
    color: #64748b;
    font-size: 11.5px;
    font-weight: 800;
    line-height: 1.35;
  }

  .loop-notification-settings-panel::-webkit-scrollbar {
    width: 8px;
  }

  .loop-notification-settings-panel::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, 0.34);
    border-radius: 999px;
  }

  .loop-notification-settings-footer {
    position: sticky;
    bottom: -1px;
    z-index: 3;
    background: linear-gradient(180deg, rgba(255,255,255,0.72), #ffffff 42%);
    border: 1px solid rgba(226,232,240,.88);
    border-radius: 18px;
    padding: 10px;
    box-shadow: 0 -10px 24px rgba(15,23,42,.06);
  }

  .loop-notification-list {
    min-height: 0;
  }

  /* =========================================================
     LOOP NOTIFICATIONS — STABLE LIST MODE FIX
     Mantiene header/filtros visibles y limita el scroll a resultados.
     ========================================================= */

  .loop-notification-panel:not(.is-settings-mode) .loop-notification-panel-head {
    flex: 0 0 auto;
  }

  .loop-notification-panel:not(.is-settings-mode) .loop-notification-toolbar {
    flex: 0 0 auto;
    position: relative;
    z-index: 2;
  }

  .loop-notification-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    background: #ffffff;
    display: flex;
    flex-direction: column;
  }

  .loop-notification-body > .loop-notification-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 10px 8px 12px;
    scrollbar-gutter: stable;
  }

  .loop-notification-body > .loop-notification-empty {
    flex: 1 1 auto;
    min-height: 236px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .loop-notification-filter-row,
  .loop-notification-tabs {
    min-width: 0;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }

  .loop-notification-filter-select {
    flex: 0 0 auto;
  }

  @media (hover: none) and (pointer: coarse) {
    .loop-notification-scroll-shell {
      grid-template-columns: minmax(0, 1fr);
    }

    .loop-notification-scroll-btn {
      display: none;
    }

    .loop-notification-tabs,
    .loop-notification-filter-row {
      padding-left: 1px;
      padding-right: 14px;
    }
  }

  .loop-notification-body .loop-notification-section-label:first-child {
    padding-top: 4px;
  }

  @media (max-width: 640px) {
    .loop-notification-panel:not(.is-settings-mode) {
      max-height: calc(100dvh - 86px);
    }

    .loop-notification-body > .loop-notification-list {
      padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    }
  }

  @media (max-width: 640px) {
    .loop-notification-panel {
      max-height: calc(100dvh - 86px);
    }

    .loop-notification-settings-panel {
      max-height: none;
      padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    }

    .loop-notification-settings-footer {
      align-items: stretch;
      flex-direction: column;
    }

    .loop-notification-save-settings {
      width: 100%;
      justify-content: center;
      min-height: 42px;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS CLEAN INBOX V3 — less noise, grouped inbox
   ========================================================= */
  .loop-notification-panel {
    width: min(420px, calc(100vw - 24px));
    border-radius: 26px;
  }

  .loop-notification-panel-head {
    padding: 18px 18px 14px;
    align-items: center;
  }

  .loop-notification-panel-head h3 {
    font-size: 23px;
    letter-spacing: -0.045em;
  }

  .loop-notification-head-actions {
    gap: 7px;
  }

  .loop-notification-toolbar-clean {
    padding: 11px 14px 12px;
    gap: 9px;
    background: #ffffff;
  }

  .loop-notification-primary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .loop-notification-segmented {
    display: inline-flex;
    align-items: center;
    padding: 3px;
    border: 1px solid rgba(226, 232, 240, .95);
    border-radius: 999px;
    background: #f8fafc;
    gap: 3px;
    min-width: 0;
  }

  .loop-notification-segmented button {
    min-height: 30px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #475569;
    padding: 0 11px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  .loop-notification-segmented button span {
    font-size: 10px;
    color: inherit;
    opacity: .82;
  }

  .loop-notification-segmented button.is-active {
    background: #00675f;
    color: #ffffff;
    box-shadow: 0 8px 18px rgba(0, 103, 95, .18);
  }

  .loop-notification-filter-toggle {
    min-height: 34px;
    border: 1px solid rgba(0, 103, 95, .16);
    border-radius: 999px;
    background: #ffffff;
    color: #00675f;
    padding: 0 12px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .loop-notification-filter-toggle.is-active {
    background: #ecfdf5;
    border-color: rgba(0, 103, 95, .26);
  }

  .loop-notification-context-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #64748b;
    font-size: 11px;
    font-weight: 850;
    line-height: 1.25;
  }

  .loop-notification-context-line span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loop-notification-context-line button {
    border: 0;
    background: transparent;
    color: #00675f;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    padding: 0;
    white-space: nowrap;
  }

  .loop-notification-filter-drawer {
    border: 1px solid rgba(226, 232, 240, .92);
    border-radius: 18px;
    background: linear-gradient(180deg, #ffffff, #fbfffd);
    padding: 11px;
    display: grid;
    gap: 11px;
    box-shadow: 0 14px 34px rgba(15, 23, 42, .06);
  }

  .loop-notification-filter-group {
    display: grid;
    gap: 7px;
  }

  .loop-notification-filter-group > span {
    color: #64748b;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  .loop-notification-filter-grid {
    display: flex;
    gap: 7px;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 2px;
  }

  .loop-notification-filter-grid::-webkit-scrollbar { display: none; }

  .loop-notification-filter-pill {
    flex: 0 0 auto;
    min-height: 30px;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    background: #ffffff;
    color: #475569;
    padding: 0 10px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .loop-notification-filter-pill small {
    font-size: 10px;
    opacity: .76;
  }

  .loop-notification-filter-pill.is-active {
    background: #00675f;
    border-color: #00675f;
    color: #ffffff;
  }

  .loop-notification-body > .loop-notification-list {
    padding: 9px 10px 13px;
    gap: 5px;
  }

  .loop-notification-section-label {
    padding: 10px 5px 5px;
    color: #94a3b8;
    font-size: 10px;
    letter-spacing: .10em;
  }

  .loop-notification-item-clean {
    position: relative;
    grid-template-columns: 8px 34px minmax(0, 1fr) auto;
    gap: 9px;
    align-items: center;
    padding: 10px 8px;
    border-radius: 18px;
    background: transparent;
    border-color: transparent;
  }

  .loop-notification-item-clean:hover {
    background: #f8fafc;
  }

  .loop-notification-item-clean.is-unread {
    background: rgba(240,253,250,.74);
    border-color: rgba(20,184,166,.18);
  }

  .loop-notification-item-clean.is-grouped {
    background: linear-gradient(135deg, rgba(240,253,250,.92), rgba(255,255,255,.98));
    border-color: rgba(20,184,166,.20);
  }

  .loop-notification-unread-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: transparent;
    display: inline-block;
  }

  .loop-notification-item-clean.is-unread .loop-notification-unread-dot {
    background: #00796f;
  }

  .loop-notification-item-clean .loop-notification-icon {
    width: 34px;
    height: 34px;
    border-radius: 13px;
    font-size: 13px;
    box-shadow: 0 8px 18px rgba(0, 103, 95, .16);
  }

  .loop-notification-item-clean .loop-notification-copy strong {
    font-size: 13px;
    margin-bottom: 3px;
  }

  .loop-notification-item-clean .loop-notification-copy p {
    font-size: 11.5px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .loop-notification-item-clean .loop-notification-copy em {
    margin-top: 4px;
    font-size: 11px;
  }

  .loop-notification-side-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;
    min-width: 34px;
  }

  .loop-notification-side-meta time {
    color: #94a3b8;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .loop-notification-side-meta b {
    min-width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #ecfdf5;
    border: 1px solid rgba(20,184,166,.22);
    color: #00675f;
    font-size: 11px;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .loop-notification-collapsible-card {
    padding: 0;
    overflow: hidden;
  }

  .loop-notification-collapsible-head {
    width: 100%;
    border: 0;
    background: transparent;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    text-align: left;
    cursor: pointer;
    color: inherit;
  }

  .loop-notification-collapsible-head strong {
    display: block;
    font-size: 13px;
    font-weight: 950;
    color: #0f172a;
  }

  .loop-notification-collapsible-head small {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
  }

  .loop-notification-collapsible-head b {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #00675f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .loop-notification-collapsible-card .loop-notification-device-list {
    padding: 0 12px 12px;
  }

  @media (max-width: 640px) {
    .loop-notification-primary-row {
      gap: 8px;
    }

    .loop-notification-segmented button {
      padding: 0 9px;
    }

    .loop-notification-item-clean {
      grid-template-columns: 7px 32px minmax(0, 1fr) auto;
      gap: 8px;
      padding: 10px 7px;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS CLEAN INBOX V4 — calm settings + internal debug
   ========================================================= */
  .loop-notification-settings-panel-v4 {
    gap: 9px;
    padding: 12px;
  }

  .loop-notification-settings-overview-card {
    border: 1px solid rgba(20,184,166,.20);
    background:
      radial-gradient(circle at 0% 0%, rgba(20,184,166,.12), transparent 38%),
      linear-gradient(180deg, #ffffff, #fbfffd);
    border-radius: 20px;
    padding: 13px;
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr);
    gap: 11px;
    align-items: center;
  }

  .loop-notification-settings-overview-icon {
    width: 38px;
    height: 38px;
    border-radius: 15px;
    display: grid;
    place-items: center;
    background: #ecfdf5;
    color: #00675f;
    font-size: 17px;
    font-weight: 950;
  }

  .loop-notification-settings-overview-copy {
    min-width: 0;
  }

  .loop-notification-settings-overview-copy span {
    display: block;
    color: #00675f;
    font-size: 10px;
    letter-spacing: .10em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 3px;
  }

  .loop-notification-settings-overview-copy strong {
    display: block;
    color: #0f172a;
    font-size: 15px;
    line-height: 1.1;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loop-notification-settings-overview-copy p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 11.5px;
    line-height: 1.25;
    font-weight: 820;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loop-notification-quiet-card {
    padding: 0;
    overflow: hidden;
    border-radius: 18px;
  }

  .loop-notification-quiet-card .loop-notification-collapsible-head {
    min-height: 62px;
    padding: 13px 14px;
  }

  .loop-notification-quiet-card .loop-notification-collapsible-head strong {
    font-size: 13.5px;
  }

  .loop-notification-quiet-card .loop-notification-collapsible-head small {
    max-width: 285px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .loop-notification-quiet-card-body {
    display: grid;
    gap: 9px;
    padding: 0 12px 12px;
  }

  .loop-notification-devices-mini {
    display: grid;
    gap: 8px;
  }

  .loop-notification-inline-link {
    width: 100%;
    border: 1px dashed rgba(0,103,95,.20);
    background: #f8fafc;
    color: #00675f;
    border-radius: 14px;
    min-height: 38px;
    padding: 0 12px;
    text-align: left;
    font-size: 11.5px;
    font-weight: 950;
    cursor: pointer;
  }

  .loop-notification-settings-footer-v4 {
    border-radius: 18px;
    margin-top: 0;
  }

  .loop-notification-settings-footer-v4 .loop-notification-save-settings {
    min-width: 92px;
  }

  @media (max-width: 640px) {
    .loop-notification-settings-overview-copy p,
    .loop-notification-quiet-card .loop-notification-collapsible-head small {
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS CLEAN INBOX V4.1 — advanced settings scroll clearance
   Cuando Opciones avanzadas abre dispositivos, deja espacio real para
   que nada quede tapado por el footer sticky de Guardar.
   ========================================================= */
  .loop-notification-panel.is-settings-mode {
    max-height: min(760px, calc(100dvh - 54px));
  }

  .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
    padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    scroll-padding-bottom: calc(126px + env(safe-area-inset-bottom, 0px));
  }

  .loop-notification-settings-panel.loop-notification-settings-panel-v4::after {
    content: "";
    display: block;
    min-height: calc(76px + env(safe-area-inset-bottom, 0px));
  }

  .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
    bottom: 8px;
    z-index: 8;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
  }

  .loop-notification-devices-mini .loop-notification-device-list {
    max-height: 210px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-right: 2px;
    padding-bottom: 8px;
    scrollbar-gutter: stable;
  }

  .loop-notification-devices-mini .loop-notification-device-list::-webkit-scrollbar {
    width: 7px;
  }

  .loop-notification-devices-mini .loop-notification-device-list::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, .28);
    border-radius: 999px;
  }

  .loop-notification-quiet-card:last-of-type {
    scroll-margin-bottom: calc(132px + env(safe-area-inset-bottom, 0px));
  }

  @media (max-width: 640px) {
    .loop-notification-panel.is-settings-mode {
      max-height: calc(100dvh - 74px);
    }

    .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
      padding-bottom: calc(112px + env(safe-area-inset-bottom, 0px));
      scroll-padding-bottom: calc(144px + env(safe-area-inset-bottom, 0px));
    }

    .loop-notification-settings-panel.loop-notification-settings-panel-v4::after {
      min-height: calc(88px + env(safe-area-inset-bottom, 0px));
    }

    .loop-notification-devices-mini .loop-notification-device-list {
      max-height: 185px;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS CLEAN INBOX V4.2 — footer no-overlap
   El footer sticky estaba tapando Opciones avanzadas. En Ajustes,
   Guardar vuelve al flujo normal para que todo pueda verse completo.
   ========================================================= */
  .loop-notification-panel.is-settings-mode {
    max-height: min(780px, calc(100dvh - 44px));
  }

  .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)) !important;
    scroll-padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px)) !important;
  }

  .loop-notification-settings-panel.loop-notification-settings-panel-v4::after {
    content: none !important;
    display: none !important;
    min-height: 0 !important;
  }

  .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
    position: static !important;
    bottom: auto !important;
    z-index: 1 !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    box-shadow: 0 10px 24px rgba(15,23,42,.055) !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-quiet-card:last-of-type {
    scroll-margin-bottom: 24px !important;
  }

  .loop-notification-devices-mini .loop-notification-device-list {
    max-height: 168px !important;
    overflow-y: auto !important;
    padding-bottom: 4px !important;
  }

  @media (max-width: 640px) {
    .loop-notification-panel.is-settings-mode {
      max-height: calc(100dvh - 52px);
    }

    .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
      padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)) !important;
      scroll-padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px)) !important;
    }

    .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
      flex-direction: row !important;
      align-items: center !important;
    }

    .loop-notification-settings-footer-v4 .loop-notification-save-settings {
      width: auto !important;
      min-width: 100px !important;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS CLEAN INBOX V4.3 — devices full scroll
   La lista de dispositivos ya no tiene scroll interno ni altura fija.
   Todo Opciones avanzadas corre dentro del scroll principal del panel,
   así ningún dispositivo queda cortado antes del botón Guardar.
   ========================================================= */
  .loop-notification-panel.is-settings-mode {
    max-height: min(820px, calc(100dvh - 28px)) !important;
  }

  .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
    overflow-y: auto !important;
    padding-bottom: calc(22px + env(safe-area-inset-bottom, 0px)) !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-devices-mini {
    display: grid !important;
    gap: 8px !important;
    padding-bottom: 8px !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-devices-mini .loop-notification-device-list {
    max-height: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
    padding-bottom: 8px !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-devices-mini .loop-notification-device-row:last-child {
    margin-bottom: 2px !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
    margin-top: 10px !important;
    flex-shrink: 0 !important;
  }

  @media (max-width: 640px) {
    .loop-notification-panel.is-settings-mode {
      max-height: calc(100dvh - 36px) !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-devices-mini .loop-notification-device-list {
      max-height: none !important;
      overflow: visible !important;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS V61 — settings true fit + single scroll contract
   Settings now uses a real internal scroll area and a fixed footer row.
   ========================================================= */
  .loop-notification-panel.is-settings-mode {
    width: min(460px, calc(100vw - 24px)) !important;
    height: min(820px, calc(100dvh - var(--loop-notification-top, 82px) - 12px)) !important;
    max-height: none !important;
    min-height: 360px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-panel-head {
    flex: 0 0 auto !important;
  }

  .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 0 !important;
    padding: 0 !important;
    border-bottom: 0 !important;
    background: linear-gradient(180deg, #ffffff 0%, #fbfffd 100%) !important;
  }

  .loop-notification-settings-scroll {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    overscroll-behavior: contain !important;
    -webkit-overflow-scrolling: touch !important;
    display: grid !important;
    gap: 10px !important;
    padding: 12px 12px 10px !important;
    scrollbar-gutter: stable !important;
  }

  .loop-notification-settings-scroll::-webkit-scrollbar {
    width: 8px !important;
  }

  .loop-notification-settings-scroll::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, 0.34) !important;
    border-radius: 999px !important;
  }

  .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
    flex: 0 0 auto !important;
    position: relative !important;
    bottom: auto !important;
    z-index: 4 !important;
    margin: 0 12px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    padding: 10px !important;
    border-radius: 18px !important;
    background: rgba(255,255,255,.98) !important;
    box-shadow: 0 -12px 28px rgba(15,23,42,.055) !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-devices-mini .loop-notification-device-list {
    max-height: none !important;
    overflow: visible !important;
    padding-bottom: 4px !important;
  }

  @media (min-width: 641px) and (max-width: 1100px) {
    .loop-notification-panel.is-settings-mode {
      left: 50% !important;
      right: auto !important;
      transform: translateX(-50%) !important;
      width: min(560px, calc(100vw - 32px)) !important;
      height: min(820px, calc(100dvh - var(--loop-notification-top, 82px) - 14px)) !important;
    }
  }

  @media (max-width: 640px) {
    .loop-notification-panel.is-settings-mode {
      top: calc(env(safe-area-inset-top, 0px) + 8px) !important;
      left: 8px !important;
      right: 8px !important;
      width: auto !important;
      height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px) !important;
      max-height: none !important;
      min-height: 0 !important;
      border-radius: 24px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-panel-head {
      padding: 14px 14px 11px !important;
      align-items: flex-start !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-panel-head h3 {
      font-size: 20px !important;
    }

    .loop-notification-settings-scroll {
      padding: 10px 10px 8px !important;
      gap: 9px !important;
    }

    .loop-notification-toggle-grid,
    .loop-notification-business-grid {
      grid-template-columns: 1fr !important;
    }

    .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
      margin: 0 10px calc(10px + env(safe-area-inset-bottom, 0px)) !important;
      flex-direction: row !important;
      align-items: center !important;
      gap: 8px !important;
    }

    .loop-notification-settings-footer-v4 .loop-notification-save-settings {
      width: auto !important;
      min-width: 98px !important;
      min-height: 40px !important;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS V62 — dedicated settings surface
   Recommendation implemented: settings is no longer a small bell popover.
   It becomes a true fit modal/sheet with one real scroll area and a fixed footer.
   ========================================================= */
  .loop-notification-panel.is-settings-mode {
    top: max(8px, env(safe-area-inset-top, 0px)) !important;
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
    width: min(620px, calc(100vw - 28px)) !important;
    height: calc(100dvh - max(8px, env(safe-area-inset-top, 0px)) - max(10px, env(safe-area-inset-bottom, 0px))) !important;
    max-height: none !important;
    min-height: 0 !important;
    border-radius: 28px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    touch-action: none !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-panel-head {
    flex: 0 0 auto !important;
    padding: 16px 18px 13px !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-head-actions {
    flex: 0 0 auto !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    max-height: none !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 0 !important;
    gap: 0 !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-scroll {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    overflow-y: scroll !important;
    overflow-x: hidden !important;
    touch-action: pan-y !important;
    overscroll-behavior-y: contain !important;
    -webkit-overflow-scrolling: touch !important;
    padding: 12px 14px 14px !important;
    display: grid !important;
    align-content: start !important;
    gap: 10px !important;
    scrollbar-gutter: stable both-edges !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
    flex: 0 0 auto !important;
    position: relative !important;
    bottom: auto !important;
    margin: 0 14px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    padding: 10px !important;
    border-radius: 18px !important;
    background: #ffffff !important;
    box-shadow: 0 -10px 26px rgba(15,23,42,.07) !important;
    z-index: 5 !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-quiet-card {
    min-width: 0 !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-quiet-card-body {
    min-width: 0 !important;
    overflow: visible !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-toggle-grid,
  .loop-notification-panel.is-settings-mode .loop-notification-business-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-setting-option {
    min-width: 0 !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-setting-option:not(.is-active) {
    background: #ffffff !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-devices-mini .loop-notification-device-list {
    max-height: none !important;
    overflow: visible !important;
  }

  @media (max-width: 740px) {
    .loop-notification-panel.is-settings-mode {
      top: max(6px, env(safe-area-inset-top, 0px)) !important;
      left: 6px !important;
      right: 6px !important;
      transform: none !important;
      width: auto !important;
      height: calc(100dvh - max(6px, env(safe-area-inset-top, 0px)) - max(8px, env(safe-area-inset-bottom, 0px))) !important;
      border-radius: 24px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-panel-head {
      padding: 13px 13px 10px !important;
      align-items: flex-start !important;
      gap: 8px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-panel-head h3 {
      font-size: 20px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-head-actions {
      gap: 6px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-settings-btn,
    .loop-notification-panel.is-settings-mode .loop-notification-mark-all {
      padding: 7px 9px !important;
      font-size: 10.5px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-settings-scroll {
      padding: 9px 9px 10px !important;
      gap: 8px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-toggle-grid,
    .loop-notification-panel.is-settings-mode .loop-notification-business-grid {
      grid-template-columns: 1fr !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
      margin: 0 9px calc(8px + env(safe-area-inset-bottom, 0px)) !important;
      padding: 9px !important;
      gap: 8px !important;
      flex-direction: row !important;
      align-items: center !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-settings-status {
      font-size: 10.5px !important;
      line-height: 1.2 !important;
    }
  }

  @media (min-width: 741px) and (max-width: 1180px) {
    .loop-notification-panel.is-settings-mode {
      width: min(640px, calc(100vw - 32px)) !important;
      height: calc(100dvh - max(10px, env(safe-area-inset-top, 0px)) - max(14px, env(safe-area-inset-bottom, 0px))) !important;
    }
  }


/* =========================================================
   LOOP NOTIFICATIONS V63 — no shrink + header-safe settings surface
   Fixes:
   - Settings surface starts below the real app/header trigger, never behind it.
   - Uses top + bottom instead of competing height/max-height rules.
   - Expanded cards do not shrink when all sections are open.
   - Only the settings-scroll area scrolls; footer stays visible without covering content.
   ========================================================= */
  .loop-notification-panel.is-settings-mode {
    top: max(
      var(--loop-notification-top, 82px),
      calc(var(--loop-mobile-header-total, 0px) + 8px),
      calc(var(--loop-native-header-h, 0px) + 8px),
      calc(env(safe-area-inset-top, 0px) + 8px)
    ) !important;
    bottom: max(10px, env(safe-area-inset-bottom, 0px)) !important;
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: hidden !important;
    touch-action: auto !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-panel.loop-notification-settings-panel-v4 {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-scroll {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    align-content: initial !important;
    gap: 10px !important;
    padding-bottom: 14px !important;
    scroll-padding-bottom: 18px !important;
    touch-action: pan-y !important;
    -webkit-overflow-scrolling: touch !important;
    overscroll-behavior-y: contain !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-overview-card,
  .loop-notification-panel.is-settings-mode .loop-notification-settings-card,
  .loop-notification-panel.is-settings-mode .loop-notification-quiet-card,
  .loop-notification-panel.is-settings-mode .loop-notification-quiet-card-body,
  .loop-notification-panel.is-settings-mode .loop-notification-radio-grid,
  .loop-notification-panel.is-settings-mode .loop-notification-toggle-grid,
  .loop-notification-panel.is-settings-mode .loop-notification-business-grid,
  .loop-notification-panel.is-settings-mode .loop-notification-devices-mini,
  .loop-notification-panel.is-settings-mode .loop-notification-device-list {
    flex: 0 0 auto !important;
    min-height: auto !important;
    height: auto !important;
    max-height: none !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-card,
  .loop-notification-panel.is-settings-mode .loop-notification-quiet-card {
    overflow: visible !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-setting-option,
  .loop-notification-panel.is-settings-mode .loop-notification-device-row,
  .loop-notification-panel.is-settings-mode .loop-notification-device-name,
  .loop-notification-panel.is-settings-mode .loop-notification-inline-link {
    flex: 0 0 auto !important;
    min-height: 38px !important;
  }

  .loop-notification-panel.is-settings-mode .loop-notification-settings-footer.loop-notification-settings-footer-v4 {
    flex: 0 0 auto !important;
    position: relative !important;
    bottom: auto !important;
    margin: 0 14px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    z-index: 7 !important;
  }

  @media (max-width: 740px) {
    .loop-notification-panel.is-settings-mode {
      top: max(
        var(--loop-notification-top, 70px),
        calc(var(--loop-mobile-header-total, 0px) + 6px),
        calc(var(--loop-native-header-h, 0px) + 6px),
        calc(env(safe-area-inset-top, 0px) + 6px)
      ) !important;
      left: 6px !important;
      right: 6px !important;
      bottom: max(8px, env(safe-area-inset-bottom, 0px)) !important;
      width: auto !important;
      height: auto !important;
      transform: none !important;
      border-radius: 24px !important;
    }

    .loop-notification-panel.is-settings-mode .loop-notification-settings-scroll {
      padding-bottom: 10px !important;
      scroll-padding-bottom: 14px !important;
    }
  }

`;

export default function NotificationCenter({
  db = defaultDb,
  user = null,
  userEmail = "",
  fallbackUnreadCount = 0,
  buttonClassName = "",
  badgeClassName = "",
  icon = null,
  mobile = false,
  activeBusinessId = "",
  activeBusinessName = "",
}) {
  useLanguage();
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const [actor, setActor] = useState(() => getEffectiveUser(user));
  const saved = useMemo(() => readSavedFilters(actor), []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 82, right: 18 });
  const [viewMode, setViewMode] = useState(saved.viewMode || "unread");
  const [typeFilter, setTypeFilter] = useState(saved.typeFilter || "all");
  const [businessFilter, setBusinessFilter] = useState(saved.businessFilter || "all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [itemLimit, setItemLimit] = useState(MAX_ITEMS);
  const [operationError, setOperationError] = useState("");
  const [devicesExpanded, setDevicesExpanded] = useState(false);
  const [settingsScopeExpanded, setSettingsScopeExpanded] = useState(false);
  const [settingsTypesExpanded, setSettingsTypesExpanded] = useState(false);
  const [settingsAdvancedExpanded, setSettingsAdvancedExpanded] = useState(false);
  const [deviceId] = useState(() => getOrCreateDeviceId());
  const [deviceSettings, setDeviceSettings] = useState(() => mergeDeviceSettings());
  const [deviceSettingsSaving, setDeviceSettingsSaving] = useState(false);
  const [deviceSettingsStatus, setDeviceSettingsStatus] = useState("");
  const [accessBusinessOptions, setAccessBusinessOptions] = useState([]);
  const [deviceNameDraft, setDeviceNameDraft] = useState(() => getDeviceLabel());
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [settingsBaseline, setSettingsBaseline] = useState(null);
  const inboxScroll = useRef(0);
  const settingsDirty = Boolean(settingsBaseline && (JSON.stringify(deviceSettings) !== JSON.stringify(settingsBaseline.settings) || deviceNameDraft !== settingsBaseline.name));
  const enterPreferences = () => {
    inboxScroll.current = document.querySelector('.loop-notification-body')?.scrollTop || 0;
    setSettingsBaseline({settings:deviceSettings,name:deviceNameDraft});
    setDeviceSettingsStatus(''); setSettingsScopeExpanded(true); setSettingsTypesExpanded(true); setSettingsOpen(true);
  };
  const leavePreferences = (close = false) => {
    if (deviceSettingsSaving) return;
    if (settingsDirty && !window.confirm('Tienes cambios sin guardar. ¿Descartarlos? Elige Cancelar para volver y guardarlos.')) return;
    if (settingsDirty && settingsBaseline) {setDeviceSettings(settingsBaseline.settings);setDeviceNameDraft(settingsBaseline.name);}
    setSettingsOpen(false);setSettingsBaseline(null);
    if (close) setOpen(false);
    else requestAnimationFrame(() => {const body=document.querySelector('.loop-notification-body');if(body) body.scrollTop=inboxScroll.current;});
  };
  const viewTabsRef = useRef(null);
  const typeTabsRef = useRef(null);
  const notificationLastErrorRef = useRef(null);
  const notificationLoadModeRef = useRef("init");

  useEffect(() => {
    const next = getEffectiveUser(user);
    if (next.uid || next.email) setActor(next);
    else if (userEmail) setActor((prev) => ({ ...prev, email: norm(userEmail) }));
  }, [user, userEmail]);

  useEffect(() => {
    const nextSaved = readSavedFilters(actor);
    if (nextSaved.viewMode) setViewMode(nextSaved.viewMode);
    if (nextSaved.typeFilter) setTypeFilter(nextSaved.typeFilter);
    if (nextSaved.businessFilter) setBusinessFilter(nextSaved.businessFilter);
  }, [actor.uid, actor.email]);

  useEffect(() => {
    let unsubAuth = null;
    const applyActor = (candidate = null) => {
      try {
        const next = getEffectiveUser(candidate);
        setActor((prev) => {
          const sameUid = String(prev?.uid || "") === String(next?.uid || "");
          const sameEmail = String(prev?.email || "") === String(next?.email || "");
          const sameAliases = JSON.stringify(prev?.emailAliases || []) === JSON.stringify(next?.emailAliases || []);
          if (sameUid && sameEmail && sameAliases) return prev;
          return next;
        });
      } catch (_) {}
    };

    try {
      unsubAuth = auth?.onAuthStateChanged?.((current) => {
        applyActor(current);
      });
    } catch (_) {}

    const onManualAuth = () => applyActor(null);
    try {
      window.addEventListener("manual-auth-changed", onManualAuth);
      window.addEventListener("manual-auth-detected", onManualAuth);
      window.addEventListener("loop-auth-login-success", onManualAuth);
      window.addEventListener("loop-auth-session-changed", onManualAuth);
      window.addEventListener("manual-auth-cleared", onManualAuth);
      window.addEventListener("loop-auth-cleared", onManualAuth);
      onManualAuth();
    } catch (_) {}

    return () => {
      try { if (typeof unsubAuth === "function") unsubAuth(); } catch (_) {}
      try {
        window.removeEventListener("manual-auth-changed", onManualAuth);
        window.removeEventListener("manual-auth-detected", onManualAuth);
        window.removeEventListener("loop-auth-login-success", onManualAuth);
        window.removeEventListener("loop-auth-session-changed", onManualAuth);
        window.removeEventListener("manual-auth-cleared", onManualAuth);
        window.removeEventListener("loop-auth-cleared", onManualAuth);
      } catch (_) {}
    };
  }, []);


  useEffect(() => {
    let cancelled = false;
    async function loadDeviceSettings() {
      try {
        if (!db || !actor.uid || !deviceId) return;
        let raw = null;
        if (isIosCapacitor() && fsClient && typeof fsClient.getDocByPath === "function") {
          raw = await fsClient.getDocByPath(`users/${actor.uid}/notificationDevices/${deviceId}`, { db }).catch(() => null);
          if (!raw && typeof fsClient.listCollectionByPath === "function") {
            const rows = await fsClient.listCollectionByPath(`users/${actor.uid}/notificationDevices`, {}, { db }).catch(() => []);
            raw = (Array.isArray(rows) ? rows : []).find((row) => String(row?.deviceId || row?.id || "") === String(deviceId)) || null;
          }
        } else {
          const snap = await getDocs(query(collection(db, "users", actor.uid, "notificationDevices"), where("deviceId", "==", deviceId), limit(1)));
          snap.forEach((d) => { raw = { id: d.id, ...(d.data() || {}) }; });
        }
        if (!cancelled) {
          setDeviceSettings(mergeDeviceSettings(raw || {}));
          setDeviceNameDraft(String(raw?.deviceName || getDeviceLabel()).trim() || getDeviceLabel());
        }
      } catch (err) {
        notificationLastErrorRef.current = { source: "loadDeviceSettings", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
        console.warn("[NotificationCenter] load device settings failed", err);
      }
    }
    loadDeviceSettings();
    return () => { cancelled = true; };
  }, [db, actor.uid, deviceId]);


  useEffect(() => {
    if (!db || !actor.uid) {
      setConnectedDevices([]);
      return undefined;
    }

    if (isIosCapacitor() && fsClient && typeof fsClient.listCollectionByPath === "function") {
      let cancelled = false;
      let timer = null;
      const loadDevices = async () => {
        try {
          const rowsRaw = await fsClient.listCollectionByPath(`users/${actor.uid}/notificationDevices`, {}, { db });
          const rows = (Array.isArray(rowsRaw) ? rowsRaw : [])
            .map((row, idx) => ({ id: row.id || row.docId || row.deviceId || `device_${idx}`, ...(row || {}) }))
            .sort((a, b) => timestampToMs(b.lastSeenAt || b.updatedAt) - timestampToMs(a.lastSeenAt || a.updatedAt));
          if (!cancelled) setConnectedDevices(rows);
        } catch (err) {
          notificationLastErrorRef.current = { source: "connectedDevicesIos", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
          console.warn("[NotificationCenter] connected devices iOS load failed", err);
          if (!cancelled) setConnectedDevices([]);
        }
      };
      loadDevices();
      timer = window.setInterval(loadDevices, 30000);
      return () => {
        cancelled = true;
        try { if (timer) window.clearInterval(timer); } catch (_) {}
      };
    }

    const col = collection(db, "users", actor.uid, "notificationDevices");
    const unsub = onSnapshot(col, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) => timestampToMs(b.lastSeenAt || b.updatedAt) - timestampToMs(a.lastSeenAt || a.updatedAt));
      setConnectedDevices(rows);
    }, (err) => {
      notificationLastErrorRef.current = { source: "connectedDevicesSnapshot", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
      console.warn("[NotificationCenter] connected devices listen failed", err);
      setConnectedDevices([]);
    });

    return () => {
      try { unsub(); } catch (_) {}
    };
  }, [db, actor.uid]);


  useEffect(() => {
    let cancelled = false;

    async function loadAccessBusinesses() {
      try {
        if (!db || (!actor.uid && !actor.email)) {
          if (!cancelled) setAccessBusinessOptions([]);
          return;
        }

        const effectiveUser = {
          uid: actor.uid || null,
          email: actor.email || "",
          displayName: actor.displayName || "",
        };

        const cached = getCachedUserBusinesses(effectiveUser) || [];
        const cachedOptions = mergeBusinessOptionLists(cached);
        if (!cancelled && cachedOptions.length) setAccessBusinessOptions(cachedOptions);

        const fresh = await getUserBusinessesAndRoles(db, effectiveUser, {
          preferOwnerFast: true,
          scanWorkers: true,
          iosBusinessTimeoutMs: 4500,
        });

        if (!cancelled) {
          setAccessBusinessOptions(mergeBusinessOptionLists(fresh || [], cachedOptions));
        }
      } catch (err) {
        console.warn("[NotificationCenter] load access businesses failed", err);
        if (!cancelled) setAccessBusinessOptions((prev) => prev || []);
      }
    }

    loadAccessBusinesses();
    return () => { cancelled = true; };
  }, [db, actor.uid, actor.email, actor.displayName]);

  const activeScopeId = useMemo(() => norm(activeBusinessId), [activeBusinessId]);
  const activeScopeName = useMemo(() => String(activeBusinessName || "").trim(), [activeBusinessName]);

  const notificationBusinessKey = (item = {}) => norm(item.businessId || item.data?.businessId || item.routeBusinessId || "");
  const notificationMatchesScope = (item = {}) => {
    if (!activeScopeId) return true;
    return notificationBusinessKey(item) === activeScopeId;
  };

  useEffect(() => {
    if (!db || !actor.uid) {
      notificationLoadModeRef.current = "missing_actor";
      setOperationError("No se pudieron actualizar los avisos. Conservamos los anteriores.");
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    if (isIosCapacitor() && fsClient && typeof fsClient.listCollectionByPath === "function") {
      let cancelled = false;
      let timer = null;
      const loadNotifications = async (mode = "ios_rest") => {
        try {
          notificationLoadModeRef.current = mode;
          const rows = await fsClient.listCollectionByPath(`users/${actor.uid}/notifications`, {}, { db });
          const next = (Array.isArray(rows) ? rows : [])
            .map((row, idx) => normalizeNotificationFsRow(row, row?.id || row?.docId || `notification_${idx}`))
            .sort((a, b) => Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0))
            .slice(0, itemLimit);
          if (!cancelled) {
            setItems(next);
            setLoading(false);
          }
        } catch (err) {
          notificationLastErrorRef.current = { source: "notificationsIosRest", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
          console.warn("[NotificationCenter] iOS notifications load failed", err);
          if (!cancelled) {
            setItems([]);
            setLoading(false);
          }
        }
      };
      loadNotifications("ios_rest_initial");
      timer = window.setInterval(() => {if (document.visibilityState === "visible") loadNotifications("ios_rest_poll");}, open ? 15000 : 30000);
      return () => {
        cancelled = true;
        try { if (timer) window.clearInterval(timer); } catch (_) {}
      };
    }

    notificationLoadModeRef.current = "firestore_snapshot";
    const q = query(
      collection(db, "users", actor.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(itemLimit)
    );

    const unsub = onSnapshot(q, (snap) => {
      const next = snap.docs.map((d) => normalizeNotification(d.id, d.data() || {}));
      setItems(next);
      setLoading(false);
    }, (err) => {
      notificationLastErrorRef.current = { source: "notificationsSnapshot", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
      console.warn("[NotificationCenter] listen failed", err);
      setItems([]);
      setLoading(false);
    });

    return () => unsub();
  }, [db, actor.uid, open, itemLimit]);

  useEffect(() => {
    saveFilters({ viewMode, typeFilter, businessFilter }, actor);
  }, [viewMode, typeFilter, businessFilter, actor.uid, actor.email]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") leavePreferences(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, settingsDirty, deviceSettingsSaving]);

  const scopedItems = useMemo(() => {
    return compactReminderNotifications(activeScopeId ? items.filter(notificationMatchesScope) : items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeScopeId]);

  const unreadCount = useMemo(() => {
    const count = scopedItems.filter((item) => !item.read && !item.expired).length;
    // In a business context, never fall back to the global pending count.
    // Otherwise Salón A can show Salón B's badge.
    if (activeScopeId) return count;
    return count;
  }, [scopedItems, fallbackUnreadCount, activeScopeId]);

  const notificationBusinessOptions = useMemo(() => {
    return items
      .map((item) => ({
        value: item.businessId || item.data?.businessId || "",
        label: item.businessName || item.data?.businessName || item.businessId || item.data?.businessId || "",
        source: "notifications",
      }))
      .filter((item) => String(item.value || item.label || "").trim());
  }, [items]);

  const deviceBusinessOptions = useMemo(() => {
    const activeOption = activeScopeId
      ? [{ value: activeScopeId, label: activeScopeName || activeScopeId, source: "active" }]
      : [];
    return mergeBusinessOptionLists(accessBusinessOptions, activeOption);
  }, [accessBusinessOptions, activeScopeId, activeScopeName]);

  const businessOptions = useMemo(() => {
    return mergeBusinessOptionLists(deviceBusinessOptions, notificationBusinessOptions);
  }, [deviceBusinessOptions, notificationBusinessOptions]);

  const hasBusinessNotificationScope = deviceBusinessOptions.length > 0;

  useEffect(() => {
    if (activeScopeId && businessFilter !== "all") {
      setBusinessFilter("all");
      return;
    }
    if (businessFilter === "all") return;
    if (!businessOptions.some((item) => item.value === businessFilter)) setBusinessFilter("all");
  }, [businessFilter, businessOptions, activeScopeId]);

  const stats = useMemo(() => {
    return {
      unread: scopedItems.filter((item) => !item.read && !item.expired).length,
      all: scopedItems.length,
      bookings: scopedItems.filter((item) => item.category === "bookings").length,
      reminders: scopedItems.filter((item) => item.category === "reminders").length,
      changes: scopedItems.filter((item) => item.category === "changes").length,
      reviews: scopedItems.filter((item) => item.category === "reviews").length,
      payments: scopedItems.filter((item) => item.category === "payments").length,
      inventory: scopedItems.filter((item) => item.category === "inventory").length,
      benefits: scopedItems.filter((item) => item.category === "benefits").length,
      other: scopedItems.filter((item) => item.category === "other").length,
    };
  }, [scopedItems]);

  const filteredItems = useMemo(() => {
    return scopedItems.filter((item) => {
      if (viewMode === "unread" && (item.read || item.expired)) return false;
      if (typeFilter !== "all" && item.category !== typeFilter) return false;
      if (!activeScopeId && businessFilter !== "all") {
        const key = String(item.businessId || item.businessName || "").trim();
        if (key !== businessFilter) return false;
      }
      return true;
    });
  }, [scopedItems, viewMode, typeFilter, businessFilter, activeScopeId]);

  const compactFilteredItems = useMemo(() => {
    const groups = new Map();
    filteredItems.forEach((item) => {
      if (!shouldCompactNotification(item)) return;
      const key = [item.category, item.type, item.businessId || item.data?.businessId || item.businessName || item.data?.businessName || "business", notificationDayBucket(item)].join("|");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });

    const emitted = new Set();
    return filteredItems.map((item) => {
      if (!shouldCompactNotification(item)) return item;
      const key = [item.category, item.type, item.businessId || item.data?.businessId || item.businessName || item.data?.businessName || "business", notificationDayBucket(item)].join("|");
      const group = groups.get(key) || [];
      if (group.length <= 1) return item;
      if (emitted.has(key)) return null;
      emitted.add(key);
      const latest = group.reduce((best, current) => Number(current.createdAtMs || 0) > Number(best.createdAtMs || 0) ? current : best, group[0]);
      const unread = group.some((row) => !row.read);
      return {
        ...latest,
        id: `group_${key}`,
        title: `${group.length} servicios por calificar`,
        body: compactNotificationBody(group),
        businessName: notificationBusinessLabel(latest),
        read: !unread,
        isCompactGroup: true,
        compactCount: group.length,
        compactItems: group,
        sourceItem: group.find((row) => !row.read) || latest,
      };
    }).filter(Boolean);
  }, [filteredItems]);

  const groupedItems = useMemo(() => {
    const today = new Date();
    const todayKey = today.toDateString();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toDateString();

    const groups = [];
    let currentKey = null;
    let current = null;

    compactFilteredItems.forEach((item) => {
      const ms = item.createdAtMs;
      let label = "Recientes";
      if (ms) {
        const d = new Date(ms);
        const key = d.toDateString();
        if (key === todayKey) label = "Hoy";
        else if (key === yesterdayKey) label = "Ayer";
        else label = d.toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short" });
      }
      if (label !== currentKey) {
        currentKey = label;
        current = { label, items: [] };
        groups.push(current);
      }
      current.items.push(item);
    });
    return groups;
  }, [compactFilteredItems]);

  const updatePanelPosition = () => {
    try {
      const rect = triggerRef.current?.getBoundingClientRect?.();
      if (!rect) return;
      const right = Math.max(8, window.innerWidth - rect.right);
      const top = Math.max(68, rect.bottom + 12);
      setPanelPos({ top, right });
    } catch (_) {}
  };

  const toggleOpen = () => {
    if (open) { leavePreferences(true); return; }
    updatePanelPosition();
    setOpen(true);
  };

  const selectedBusinessScopes = useMemo(() => {
    if (deviceSettings.scopeMode === "all") return [];
    if (deviceSettings.scopeMode === "active" && activeScopeId) return [activeScopeId];
    return Array.isArray(deviceSettings.businessScopes) ? deviceSettings.businessScopes : [];
  }, [deviceSettings.scopeMode, deviceSettings.businessScopes, activeScopeId]);

  const updateDeviceSettingsDraft = (patch = {}) => {
    setDeviceSettings((current) => mergeDeviceSettings({ ...current, ...patch }));
  };

  const toggleDeviceType = (key) => {
    setDeviceSettings((current) => mergeDeviceSettings({
      ...current,
      notificationTypes: {
        ...(current.notificationTypes || {}),
        [key]: !(current.notificationTypes || {})[key],
      },
    }));
  };

  const toggleBusinessScope = (businessId) => {
    const cleanId = String(businessId || "").trim();
    if (!cleanId) return;
    setDeviceSettings((current) => {
      const currentScopes = Array.isArray(current.businessScopes) ? current.businessScopes : [];
      const nextScopes = currentScopes.includes(cleanId) ? currentScopes.filter((id) => id !== cleanId) : [...currentScopes, cleanId];
      return mergeDeviceSettings({ ...current, scopeMode: "custom", businessScopes: nextScopes });
    });
  };

  const saveDeviceSettings = async () => {
    try {
      if (!actor.uid || !db) return;
      setDeviceSettingsSaving(true);
      setDeviceSettingsStatus("Guardando este dispositivo...");
      const iosNative = isIosCapacitor();
      const token = iosNative ? "" : await maybeGetBrowserPushToken();
      const effectiveScopeMode = hasBusinessNotificationScope ? deviceSettings.scopeMode : "all";
      const scopes = effectiveScopeMode === "all" ? [] : selectedBusinessScopes;
      const cleanDeviceName = String(deviceNameDraft || getDeviceLabel()).trim() || getDeviceLabel();
      const payload = {
        version: DEVICE_SETTINGS_VERSION,
        deviceId,
        deviceName: cleanDeviceName,
        platform: iosNative ? "ios" : "web",
        enabled: true,
        fcmToken: token || deviceSettings.fcmToken || deviceSettings.token || null,
        notifyAllBusinesses: effectiveScopeMode === "all",
        scopeMode: effectiveScopeMode,
        businessScopes: scopes,
        notificationTypes: deviceSettings.notificationTypes || DEFAULT_DEVICE_SETTINGS.notificationTypes,
        muteManualCreatedByMe: deviceSettings.muteManualCreatedByMe !== false,
        activeBusinessId: activeScopeId || null,
        activeBusinessName: activeScopeName || null,
        lastSeenAt: notificationClientTimestamp(iosNative),
        updatedAt: notificationClientTimestamp(iosNative),
      };
      if (iosNative && fsClient && typeof fsClient.setDocByPath === "function") {
        await fsClient.setDocByPath(`users/${actor.uid}/notificationDevices/${deviceId}`, payload, { merge: true, db });
      } else {
        await setDoc(doc(db, "users", actor.uid, "notificationDevices", deviceId), payload, { merge: true });
      }
      if (token) {
        const tokenPayload = {
          token,
          deviceId,
          enabled: true,
          notifyAllBusinesses: payload.notifyAllBusinesses,
          scopeMode: payload.scopeMode,
          businessScopes: payload.businessScopes,
          notificationTypes: payload.notificationTypes,
          muteManualCreatedByMe: payload.muteManualCreatedByMe,
          deviceName: payload.deviceName,
          platform: payload.platform,
          updatedAt: notificationClientTimestamp(iosNative),
          lastSeenAt: notificationClientTimestamp(iosNative),
        };
        if (iosNative && fsClient && typeof fsClient.setDocByPath === "function") {
          await fsClient.setDocByPath(`users/${actor.uid}/fcmTokens/${safeTokenDocId(token)}`, tokenPayload, { merge: true, db });
        } else {
          await setDoc(doc(db, "users", actor.uid, "fcmTokens", safeTokenDocId(token)), tokenPayload, { merge: true });
        }
      }
      setDeviceSettings(mergeDeviceSettings(payload));
      setSettingsBaseline({settings:mergeDeviceSettings(payload),name:deviceNameDraft});
      setDeviceSettingsStatus(token ? "Listo. Este dispositivo ya tiene sus preferencias." : (iosNative ? "Guardado. El token push nativo se registra desde la app." : "Guardado. Activa permisos del navegador para recibir push aquí."));
    } catch (err) {
      notificationLastErrorRef.current = { source: "saveDeviceSettings", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
      console.warn("[NotificationCenter] save device settings failed", err);
      setDeviceSettingsStatus("No se pudo guardar. Revisa permisos o conexión.");
    } finally {
      setDeviceSettingsSaving(false);
    }
  };

  const markRead = async (item) => {
    if (item.duplicateItems?.length) {await Promise.all(item.duplicateItems.map(markRead)); return;}
    try {
      if (!actor.uid || !item?.id) return;
      const iosNative = isIosCapacitor();
      const payload = {
        read: true,
        readAt: notificationClientTimestamp(iosNative),
        updatedAt: notificationClientTimestamp(iosNative),
      };
      if (iosNative && fsClient && typeof fsClient.updateDocByPath === "function") {
        await fsClient.updateDocByPath(`users/${actor.uid}/notifications/${item.id}`, payload, { db });
      } else {
        await updateDoc(doc(db, "users", actor.uid, "notifications", item.id), payload);
      }
    } catch (err) {
      notificationLastErrorRef.current = { source: "markRead", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
      setOperationError("No se pudo marcar el aviso como leído. Intenta de nuevo.");
      console.warn("[NotificationCenter] markRead failed", err);
    }
  };

  const markAllRead = async () => {
    try {
      if (!actor.uid || !db) return;
      const unreadItems = scopedItems.flatMap(item => item.duplicateItems || [item]).filter(item => !item.read).slice(0, 400);
      if (!unreadItems.length) return;
      const iosNative = isIosCapacitor();
      if (iosNative && fsClient && typeof fsClient.updateDocByPath === "function") {
        await Promise.all(unreadItems.map((item) => fsClient.updateDocByPath(`users/${actor.uid}/notifications/${item.id}`, {
          read: true,
          readAt: notificationClientTimestamp(true),
          updatedAt: notificationClientTimestamp(true),
        }, { db }).catch((err) => {
          console.warn("[NotificationCenter] markAllRead item failed", item.id, err);
          setOperationError("Algunos avisos no pudieron marcarse como leídos.");
          return null;
        })));
        return;
      }
      const batch = writeBatch(db);
      unreadItems.forEach((item) => batch.update(doc(db, "users", actor.uid, "notifications", item.id), {
        read: true,
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }));
      await batch.commit();
    } catch (err) {
      notificationLastErrorRef.current = { source: "markAllRead", message: err?.message || String(err), code: err?.code || "unknown", time: new Date().toISOString() };
      setOperationError("No se pudieron marcar todos los avisos. Intenta de nuevo.");
      console.warn("[NotificationCenter] markAllRead failed", err);
    }
  };

  const openNotification = (item) => {
    if (!item) return;
    const sourceItem = item.sourceItem || item;
    const payload = {
      ...(sourceItem.data || {}),
      type: sourceItem.type || sourceItem.data?.type || sourceItem.data?.notificationType || "notification",
      notificationType: sourceItem.type || sourceItem.data?.notificationType || sourceItem.data?.type || "notification",
      businessId: sourceItem.businessId || sourceItem.data?.businessId || "",
      bookingId: sourceItem.bookingId || sourceItem.data?.bookingId || "",
      businessName: sourceItem.businessName || sourceItem.data?.businessName || "",
      targetRole: sourceItem.targetRole || sourceItem.data?.targetRole || "",
      route: sourceItem.route || sourceItem.data?.route || "",
    };

    try {
      if (typeof window !== "undefined") {
        window.__LOOP_NOTIFICATION_CENTER_LAST_CLICK__ = {
          itemId: sourceItem.id || item.id || "",
          groupId: item.isCompactGroup ? item.id : "",
          payload,
          time: new Date().toISOString(),
        };
      }
    } catch (_) {}

    // Do not block navigation/debug on Firestore latency. If mark-read fails, the
    // route still opens and the console debug is still written by openLoopNotificationRoute.
    if (!sourceItem.read) markRead(sourceItem);
    setOpen(false);
    openLoopNotificationRoute(payload, { navigate });
  };

  const visibleSummaryBase = viewMode === "unread"
    ? `${stats.unread} pendiente${stats.unread === 1 ? "" : "s"} de ${stats.all}`
    : `${filteredItems.length} aviso${filteredItems.length === 1 ? "" : "s"}`;
  const visibleSummary = activeScopeId && activeScopeName
    ? `${visibleSummaryBase} · ${activeScopeName}`
    : visibleSummaryBase;

  const scrollToolbarRow = (ref, direction = 1) => {
    try {
      const el = ref?.current;
      if (!el) return;
      const amount = Math.max(120, Math.round(el.clientWidth * 0.62));
      el.scrollBy({ left: amount * direction, behavior: "smooth" });
    } catch (_) {}
  };

  const handleToolbarWheel = (event) => {
    try {
      const el = event.currentTarget;
      if (!el) return;
      if (Math.abs(event.deltaY || 0) > Math.abs(event.deltaX || 0)) {
        el.scrollLeft += event.deltaY;
        event.preventDefault();
      }
    } catch (_) {}
  };

  const activeFilterCount = [typeFilter !== "all", !activeScopeId && businessFilter !== "all"].filter(Boolean).length;
  const selectedFilterLabel = [
    typeFilter !== "all" ? labelForCategory(typeFilter) : "Todos los tipos",
    !activeScopeId && businessFilter !== "all" ? (businessOptions.find((option) => option.value === businessFilter)?.label || "Negocio") : "Todos los negocios",
  ].join(" · ");

  const clearNotificationFilters = () => {
    setTypeFilter("all");
    setBusinessFilter("all");
  };

  const settingsTypeRows = useMemo(() => (hasBusinessNotificationScope ? [
    ["bookings", "Reservas"],
    ["reminders", "Recordatorios"],
    ["changes", "Cambios"],
    ["payments", "Pagos"],
    ["inventory", "Inventario"],
    ["benefits", "Beneficios"],
    ["reviews", "Valoraciones"],
  ] : [
    ["bookings", "Mis reservas"],
    ["reminders", "Recordatorios"],
    ["changes", "Cambios"],
    ["benefits", "Beneficios"],
    ["reviews", "Valoraciones"],
  ]), [hasBusinessNotificationScope]);

  const activeTypeLabels = useMemo(() => settingsTypeRows
    .filter(([key]) => deviceSettings.notificationTypes?.[key] !== false)
    .map(([, label]) => label), [settingsTypeRows, deviceSettings.notificationTypes]);

  const deviceScopeSummary = useMemo(() => {
    if (!hasBusinessNotificationScope) return "Mis reservas como cliente";
    if (deviceSettings.scopeMode === "all") return "Todos los negocios";
    if (deviceSettings.scopeMode === "active") return activeScopeName || "Solo el negocio abierto";
    if (selectedBusinessScopes.length) return deviceScopeLabel({ ...deviceSettings, businessScopes: selectedBusinessScopes, scopeMode: "custom" }, deviceBusinessOptions);
    return "Negocios específicos";
  }, [hasBusinessNotificationScope, deviceSettings, selectedBusinessScopes, deviceBusinessOptions, activeScopeName]);

  const notificationHealthSnapshot = useMemo(() => ({
    version: "clean_inbox_v4_apple_ios_v2",
    isIosCapacitor: isIosCapacitor(),
    manualAuth: (() => {
      try {
        const ma = getManualAuth();
        const manualUser = manualAuthToNotificationUser(ma);
        return {
          exists: Boolean(ma),
          likelyValid: Boolean(ma && isManualAuthLikelyValid(ma)),
          uid: manualUser?.uid || "",
          email: manualUser?.email || "",
          emailAliases: manualUser?.emailAliases || [],
          source: ma?.source || "",
        };
      } catch (_) {
        return { exists: false, likelyValid: false };
      }
    })(),
    actorUid: actor.uid || "",
    actorEmail: actor.email || "",
    actorEmailAliases: Array.isArray(actor.emailAliases) ? actor.emailAliases : [],
    deviceId,
    deviceName: deviceNameDraft || getDeviceLabel(),
    browserPermission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
    hasFcmTokenOnDevice: Boolean(deviceSettings.fcmToken || deviceSettings.token),
    scopeMode: deviceSettings.scopeMode || "all",
    businessScopes: Array.isArray(deviceSettings.businessScopes) ? deviceSettings.businessScopes : [],
    activeBusinessId: activeScopeId || "",
    activeBusinessName: activeScopeName || "",
    notificationTypes: deviceSettings.notificationTypes || {},
    counts: { ...stats, totalItems: items.length, scopedItems: scopedItems.length, filtered: filteredItems.length, compacted: compactFilteredItems.length },
    filters: { viewMode, typeFilter, businessFilter },
    loadMode: notificationLoadModeRef.current,
    lastError: notificationLastErrorRef.current,
    sampleItems: items.slice(0, 5).map((item) => ({ id: item.id, type: item.type, title: item.title, businessId: item.businessId || item.data?.businessId || "", read: item.read, createdAtMs: item.createdAtMs || 0 })),
    lastRoute: (() => {
      try {
        const raw = typeof localStorage !== "undefined" ? localStorage.getItem("loop_push_last_route_v1") : "";
        return raw ? JSON.parse(raw) : null;
      } catch (_) { return null; }
    })(),
    lastClick: typeof window !== "undefined" ? (window.__LOOP_NOTIFICATION_CENTER_LAST_CLICK__ || null) : null,
    generatedAt: new Date().toISOString(),
  }), [actor.uid, actor.email, actor.emailAliases, deviceId, deviceNameDraft, deviceSettings, activeScopeId, activeScopeName, stats, items, scopedItems.length, filteredItems.length, compactFilteredItems.length, viewMode, typeFilter, businessFilter]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      window.__LOOP_NOTIFICATION_CENTER_HEALTH__ = notificationHealthSnapshot;
      window.__LOOP_NOTIFICATION_CENTER_DEBUG__ = () => window.__LOOP_NOTIFICATION_CENTER_HEALTH__;
      window.__LOOP_NOTIFICATION_DEBUG__ = () => window.__LOOP_NOTIFICATION_CENTER_HEALTH__;
    } catch (_) {}
  }, [notificationHealthSnapshot]);

  const panel = open ? createPortal(
    <>
      <div className="loop-notification-panel-backdrop" onClick={() => leavePreferences(true)} />
      <section
        className={`loop-notification-panel ${settingsOpen ? "is-settings-mode" : ""}`}
        style={{
          "--loop-notification-top": `${panelPos.top}px`,
          "--loop-notification-right": `${panelPos.right}px`,
        }}
        aria-label={t("Centro de notificaciones")}
      >
        <div className="loop-notification-panel-head">
          <div>
            <span>Loop</span>
            {settingsOpen && <button type="button" className="notification-preferences-back" onClick={() => leavePreferences()}>{t("← Notificaciones")}</button>}
            <h3>{settingsOpen ? "Preferencias" : t("Notificaciones")}</h3>
            <p>{settingsOpen ? "Solo para este dispositivo" : stats.unread ? `${stats.unread} no leídas` : "Todo al día"}</p>
          </div>
          <div className="loop-notification-head-actions">
            <button type="button" aria-label={t("Cerrar notificaciones")} onClick={() => leavePreferences(true)}>✕</button>
            {!settingsOpen && <button type="button" className={`loop-notification-settings-btn ${settingsOpen ? "is-active" : ""}`} onClick={enterPreferences}>{t("Ajustes")}</button>}
            {!settingsOpen && items.some((item) => !item.read) ? (
              <button type="button" className="loop-notification-mark-all" onClick={markAllRead}>{t("Marcar lista como leída")}</button>
            ) : null}
          </div>
        </div>

        {operationError && <div className="notification-operation-error" role="alert">{operationError}<button type="button" onClick={() => setOperationError("")}>{t("Cerrar")}</button></div>}

        {settingsOpen ? (
          <div className="loop-notification-settings-panel loop-notification-settings-panel-v4">
            <div className="loop-notification-settings-scroll">
            <section className="loop-notification-settings-overview-card">
              <div className="loop-notification-settings-overview-icon">⌁</div>
              <div className="loop-notification-settings-overview-copy">
                <span>{t("Ajustes de este dispositivo")}</span>
                <strong>{deviceNameDraft || getDeviceLabel()}</strong>
                <p>{deviceScopeSummary} · {activeTypeLabels.length}{" " + t("tipo")}{activeTypeLabels.length === 1 ? "" : "s"}{" " + t("activo")}{activeTypeLabels.length === 1 ? "" : "s"}</p>
              </div>
            </section>

            <section className="loop-notification-settings-card loop-notification-collapsible-card loop-notification-quiet-card">
              <button type="button" className="loop-notification-collapsible-head" onClick={() => setSettingsScopeExpanded((v) => !v)}>
                <span>
                  <strong>{t("Recibir avisos")}</strong>
                  <small>{deviceScopeSummary}</small>
                </span>
                <b>{settingsScopeExpanded ? "−" : "+"}</b>
              </button>
              {settingsScopeExpanded ? (
                <div className="loop-notification-quiet-card-body">
                  {!hasBusinessNotificationScope ? (
                    <span className="loop-notification-client-mode-pill">{t("Modo cliente · solo tus reservas")}</span>
                  ) : (
                    <>
                      <div className="loop-notification-radio-grid">
                        <button type="button" className={`loop-notification-setting-option ${deviceSettings.scopeMode === "all" ? "is-active" : ""}`} onClick={() => updateDeviceSettingsDraft({ scopeMode: "all", businessScopes: [] })}>{t("Todos los negocios")}<span>✓</span>
                        </button>
                        <button type="button" className={`loop-notification-setting-option ${deviceSettings.scopeMode === "active" ? "is-active" : ""}`} onClick={() => updateDeviceSettingsDraft({ scopeMode: "active", businessScopes: activeScopeId ? [activeScopeId] : [] })} disabled={!activeScopeId}>{t("Solo")}{activeScopeName || "el negocio abierto"} <span>✓</span>
                        </button>
                        <button type="button" className={`loop-notification-setting-option ${deviceSettings.scopeMode === "custom" ? "is-active" : ""}`} onClick={() => updateDeviceSettingsDraft({ scopeMode: "custom" })}>{t("Elegir específicos")}<span>✓</span>
                        </button>
                      </div>
                      {deviceSettings.scopeMode === "custom" ? (
                        <div className="loop-notification-business-grid">
                          {deviceBusinessOptions.length ? deviceBusinessOptions.map((option) => (
                            <button type="button" key={option.value} className={`loop-notification-setting-option ${selectedBusinessScopes.includes(option.value) ? "is-active" : ""}`} onClick={() => toggleBusinessScope(option.value)}>
                              {option.label} <span>✓</span>
                            </button>
                          )) : <p>{t("No hay negocios administrados o asignados a esta cuenta todavía.")}</p>}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
            </section>

            <section className="loop-notification-settings-card loop-notification-collapsible-card loop-notification-quiet-card">
              <button type="button" className="loop-notification-collapsible-head" onClick={() => setSettingsTypesExpanded((v) => !v)}>
                <span>
                  <strong>{t("Tipos de avisos")}</strong>
                  <small>{activeTypeLabels.length ? activeTypeLabels.slice(0, 3).join(", ") + (activeTypeLabels.length > 3 ? ` + ${activeTypeLabels.length - 3} más` : "") : "Ninguno activo"}</small>
                </span>
                <b>{settingsTypesExpanded ? "−" : "+"}</b>
              </button>
              {settingsTypesExpanded ? (
                <div className="loop-notification-quiet-card-body">
                  <div className="loop-notification-toggle-grid">
                    {settingsTypeRows.map(([key, label]) => (
                      <button type="button" key={key} role="switch" aria-checked={deviceSettings.notificationTypes?.[key] !== false} className={`loop-notification-setting-option ${deviceSettings.notificationTypes?.[key] !== false ? "is-active" : ""}`} onClick={() => toggleDeviceType(key)}>
                        {label} <span>✓</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>

            <section className="loop-notification-settings-card loop-notification-collapsible-card loop-notification-quiet-card">
              <button type="button" className="loop-notification-collapsible-head" onClick={() => setSettingsAdvancedExpanded((v) => !v)}>
                <span>
                  <strong>{t("Opciones avanzadas")}</strong>
                  <small>{t("Nombre del dispositivo, reservas manuales y dispositivos conectados")}</small>
                </span>
                <b>{settingsAdvancedExpanded ? "−" : "+"}</b>
              </button>
              {settingsAdvancedExpanded ? (
                <div className="loop-notification-quiet-card-body">
                  <div className="loop-notification-device-name">
                    <label htmlFor="loop-notification-device-name">{t("Nombre del dispositivo")}</label>
                    <input
                      id="loop-notification-device-name"
                      className="loop-notification-device-input"
                      value={deviceNameDraft}
                      onChange={(event) => setDeviceNameDraft(event.target.value)}
                      placeholder={hasBusinessNotificationScope ? "Recepción principal, caja, laptop dueño..." : "Mi teléfono, mi laptop, mi navegador..."}
                      maxLength={64}
                    />
                  </div>

                  {hasBusinessNotificationScope ? (
                    <button type="button" className={`loop-notification-setting-option ${deviceSettings.muteManualCreatedByMe !== false ? "is-active" : ""}`} onClick={() => updateDeviceSettingsDraft({ muteManualCreatedByMe: deviceSettings.muteManualCreatedByMe === false })}>{t("No avisarme cuando yo cree una reserva manual")}<span>✓</span>
                    </button>
                  ) : null}

                  {hasBusinessNotificationScope ? (
                    <div className="loop-notification-devices-mini">
                      <button type="button" className="loop-notification-inline-link" onClick={() => setDevicesExpanded((v) => !v)}>{t("Dispositivos conectados ·")}{connectedDevices.length || 0} {devicesExpanded ? "−" : "+"}
                      </button>
                      {devicesExpanded ? (
                        <div className="loop-notification-device-list">
                          {connectedDevices.length ? connectedDevices.slice(0, 6).map((device) => {
                            const isCurrent = String(device.deviceId || device.id) === String(deviceId);
                            return (
                              <div className="loop-notification-device-row" key={device.id || device.deviceId}>
                                <div>
                                  <strong>{device.deviceName || getDeviceLabel()}</strong>
                                  <small>{deviceScopeLabel(device, deviceBusinessOptions)} · {formatDeviceSeenAt(device.lastSeenAt || device.updatedAt)}</small>
                                </div>
                                <span className="loop-notification-device-tag">{isCurrent ? "Este dispositivo" : "Guardado"}</span>
                              </div>
                            );
                          }) : (
                            <p>{t("Guarda estos ajustes para registrar este dispositivo.")}</p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            </div>

            <div className="loop-notification-settings-footer loop-notification-settings-footer-v4">
              <p className="loop-notification-settings-status">{deviceSettingsSaving || deviceSettingsStatus.startsWith("No se pudo") ? deviceSettingsStatus : settingsDirty ? "Cambios sin guardar" : deviceSettingsStatus || "Estas preferencias se aplican a este dispositivo."}</p>
              <button type="button" className="loop-notification-save-settings" onClick={saveDeviceSettings} disabled={deviceSettingsSaving || !settingsDirty}>
                {deviceSettingsSaving ? t("Guardando...") : "Guardar cambios"}
              </button>
            </div>
          </div>
        ) : null}

        {!settingsOpen ? (
          <>
        <div className="loop-notification-toolbar loop-notification-toolbar-clean">
          <div className="loop-notification-primary-row">
            <div className="loop-notification-segmented" aria-label={t("Vista de notificaciones")}>
              <button type="button" className={viewMode === "unread" ? "is-active" : ""} onClick={() => setViewMode("unread")}>{t("No leídas")}<span>{stats.unread}</span>
              </button>
              <button type="button" className={viewMode === "all" ? "is-active" : ""} onClick={() => setViewMode("all")}>{t("Todas")}<span>{stats.all}</span>
              </button>
            </div>
            <button type="button" className={`loop-notification-filter-toggle ${filtersOpen ? "is-active" : ""}`} onClick={() => setFiltersOpen((v) => !v)}>{t("Filtrar")}{activeFilterCount ? ` · ${activeFilterCount}` : ""}
            </button>
          </div>

          {(activeScopeId && activeScopeName) || activeFilterCount ? (
            <div className="loop-notification-context-line">
              <span>{activeScopeId && activeScopeName ? `Mostrando ${activeScopeName}` : selectedFilterLabel}</span>
              {activeFilterCount ? <button type="button" onClick={clearNotificationFilters}>{t("Limpiar")}</button> : null}
            </div>
          ) : null}

          {filtersOpen ? (
            <div className="loop-notification-filter-drawer">
              <div className="loop-notification-filter-group">
                <span>{t("Tipo")}</span>
                <div className="loop-notification-filter-grid">
                  {[
                    ["all", "Todos", stats.all],
                    ["bookings", "Reservas", stats.bookings],
                    ["reminders", "Recordatorios", stats.reminders],
                    ["changes", "Cambios", stats.changes],
                    ["reviews", "Valoraciones", stats.reviews],
                    ["payments", "Pagos", stats.payments],
                    ["inventory", "Inventario", stats.inventory],
                    ["benefits", "Beneficios", stats.benefits],
                    ["other", "Otros", stats.other],
                  ].filter(([key, , count]) => key === "all" || Number(count || 0) > 0).map(([key, label, count]) => (
                    <button type="button" key={key} className={`loop-notification-filter-pill ${typeFilter === key ? "is-active" : ""}`} onClick={() => setTypeFilter(key)}>
                      {label}<small>{count}</small>
                    </button>
                  ))}
                </div>
              </div>
              {!activeScopeId && businessOptions.length > 1 ? (
                <div className="loop-notification-filter-group">
                  <span>{t("Negocio")}</span>
                  <select
                    className="loop-notification-filter-select"
                    value={businessFilter}
                    onChange={(event) => setBusinessFilter(event.target.value)}
                    aria-label={t("Filtrar por negocio")}
                  >
                    <option value="all">{t("Todos los negocios")}</option>
                    {businessOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="loop-notification-body">
        {loading ? (
          <div className="loop-notification-empty">
            <div>…</div>
            <strong>{t("Cargando")}</strong>
            <p>{t("Buscando tus últimas notificaciones.")}</p>
          </div>
        ) : filteredItems.length ? (
          <div className="loop-notification-list">
            {groupedItems.map((group) => (
              <React.Fragment key={group.label}>
                <div className="loop-notification-section-label">{group.label}</div>
                {group.items.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`loop-notification-item loop-notification-item-clean ${item.read ? "" : "is-unread"} ${item.isCompactGroup ? "is-grouped" : ""}`}
                    onClick={() => openNotification(item)}
                    title={fullDateLabel(item.createdAt)}
                  >
                    <span className="loop-notification-unread-dot" aria-hidden="true" />
                    <span className="loop-notification-icon">{iconForType(item.type)}</span>
                    <span className="loop-notification-copy">
                      <strong>{item.title}</strong>
                      {item.body ? <p>{item.body}</p> : null}
                      {item.businessName ? <em>{item.businessName}</em> : null}
                    </span>
                    <span className="loop-notification-side-meta">
                      <time>{relativeTime(item.createdAt)}</time>
                      {item.isCompactGroup ? <b>{item.compactCount}</b> : null}
                    </span>
                  </button>
                ))}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="loop-notification-empty">
            <div>{scopedItems.length ? "⌁" : "✓"}</div>
            <strong>{scopedItems.length ? "No hay resultados" : "Todo al día"}</strong>
            <p>{scopedItems.length ? "Prueba cambiar los filtros o ver todas las notificaciones." : "Cuando haya nuevas reservas o avisos, aparecerán aquí."}</p>
          </div>
        )}
        {!settingsOpen && items.length >= itemLimit && itemLimit < 400 && <button type="button" className="notification-load-more" onClick={() => setItemLimit(value => value + 80)}>{t("Ver avisos anteriores")}</button>}
        </div>
          </>
        ) : null}
      </section>
    </>,
    document.body
  ) : null;

  return (
    <span className="loop-notification-center-wrap">
      <style>{styles}</style>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        className={buttonClassName}
        aria-label={t("Notificaciones")}
        aria-expanded={open}
      >
        {icon || "🔔"}
        {unreadCount > 0 ? <span className={badgeClassName}>{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
      </button>
      {panel}
    </span>
  );
}
